import React, { useState, useEffect } from 'react';
import { LayoutGrid, Lock, ClipboardList, BarChart2, TrendingUp, MessageSquare, Settings, Palette, Users, User, Folder, Bot, Download, LogOut, ChevronDown, ChevronUp, Plug, FolderOpen, Building2, UtensilsCrossed } from 'lucide-react';

const BASE_URL = 'https://qbdp-backend-production.up.railway.app/api';

const navItems = [
  { label: 'Vue generale',     icon: 'grid',  path: '/'                },
  { label: 'Acces zones',      icon: 'lock',  path: '/acces'           },
  { label: 'Historique',       icon: 'list',  path: '/historique'      },
  { label: 'Employés',         icon: 'file',  path: '/rapports'        },
  { label: 'Communications',  icon: 'mail',  path: '/communications'  },
  { label: 'Integrations',     icon: 'plug',  path: '/integrations'    },
  { label: 'Reglages',         icon: 'gear',  path: '/reglages'        },
  { label: 'Personnalisation', icon: 'brush', path: '/personnalisation'},
];

function SectionHeader({ label, color, collapsed, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 20px', cursor: 'pointer', userSelect: 'none' }}
    >
      <span style={{ fontSize: 10, fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      {collapsed
        ? <ChevronDown size={11} color={color} />
        : <ChevronUp size={11} color={color} />
      }
    </div>
  );
}

export default function Sidebar({ active, onNavigate, onLogout, user, onFilterService, employees, services, clients }) {
  const [expandedServices, setExpandedServices] = useState(false);
  const [activeService, setActiveService]       = useState(null);
  const [logoSociete, setLogoSociete]           = useState(null);
  const [nomEtab, setNomEtab]                   = useState(null);
  const [secteur, setSecteur]                   = useState(null);

  const [collapsed, setCollapsed] = useState({
    principal:     false,
    organisation:  false,
    analyse:       false,
    evenements:    false,
    reglages:      false,
    systeme:       false,
  });

  const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  const employes     = employees || [];
  const servicesList = services  || [];

  const denominationsUtilisees = servicesList
    .filter(s => employes.some(e => e.service === s.nom))
    .map(s => ({ label: s.nom, count: employes.filter(e => e.service === s.nom).length }));

  const navIcons = [LayoutGrid, Lock, ClipboardList, BarChart2, MessageSquare, Plug, Settings, Palette];

  useEffect(() => {
    const token = sessionStorage.getItem('qbdp_token');
    fetch(`${BASE_URL}/personnalisation/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const src  = d.logo_web_src || 'file1';
        const logo = src === 'file2' ? d.logo_mobile_base64 : src === 'url' ? d.logo_url : d.logo_base64;
        setLogoSociete(logo || null);
        if (d.nom_etablissement) setNomEtab(d.nom_etablissement);
        if (d.secteur_activite)  setSecteur(d.secteur_activite);
        const c   = d.couleur_principale || '#1a56db';
        const hex = c.replace('#', '');
        const r   = parseInt(hex.slice(0,2), 16);
        const g   = parseInt(hex.slice(2,4), 16);
        const b   = parseInt(hex.slice(4,6), 16);
        document.documentElement.style.setProperty('--blue', c);
        document.documentElement.style.setProperty('--blue-light', `rgba(${r},${g},${b},0.1)`);
      })
      .catch(() => {});
  }, [active]);

  const HOTEL_SECTEURS     = ['hotel', 'hotel_restaurant', 'residence', 'logis', 'rbnb'];
  const RESTAURANT_SECTEURS = ['restaurant', 'hotel_restaurant'];
  const showEtab       = !secteur || HOTEL_SECTEURS.includes(secteur);
  const showRestaurant = !secteur || RESTAURANT_SECTEURS.includes(secteur);

  const handleServiceClick = (label) => {
    setActiveService(label);
    if (onFilterService) onFilterService(label);
    onNavigate('/employes');
  };

  const s = {
    sidebar: { width: 220, height: '100vh', background: 'var(--white)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, overflow: 'hidden' },
    logo:    { padding: '12px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
    item:    (act) => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', fontSize: 13, fontWeight: act ? 600 : 400, color: act ? 'var(--blue)' : '#374151', background: act ? 'var(--blue-light)' : 'transparent', borderRadius: 8, margin: '1px 8px', cursor: 'pointer' }),
    footer:  { padding: '14px 16px', borderTop: '1px solid var(--border)' },
  };

  return (
    <aside style={s.sidebar}>
      {/* Logo */}
      <div style={s.logo}>
        {logoSociete
          ? <img src={logoSociete} style={{ maxWidth: 190, height: 90, objectFit: 'contain' }} alt="Logo" />
          : <img src="/logo-full.png" style={{ width: 190, height: 100, objectFit: 'contain' }} alt="qbdp" />
        }
      </div>

      <div style={{ padding: '12px 0', flex: 1, overflowY: 'auto' }}>

        {/* PRINCIPAL */}
        <SectionHeader label="Principal" color="var(--blue)" collapsed={collapsed.principal} onToggle={() => toggle('principal')} />
        {!collapsed.principal && navItems.slice(0,2).map((item, i) => {
          const Icon = navIcons[i];
          return (
            <div key={item.path} style={s.item(active === item.path)} onClick={() => { onNavigate(item.path); setActiveService(null); if (onFilterService) onFilterService(null); }}>
              <Icon size={15} color={active === item.path ? 'var(--blue)' : '#6b7280'} />{item.label}
            </div>
          );
        })}

        {/* ORGANISATION */}
        <div style={{ marginTop: 8 }}>
          <SectionHeader label="Organisation & Accès" color="#7c3aed" collapsed={collapsed.organisation} onToggle={() => toggle('organisation')} />
        </div>
        {!collapsed.organisation && <>
          <div onClick={() => { setActiveService(null); if (onFilterService) onFilterService(null); onNavigate('/employes'); }}
            style={{ ...s.item(!activeService && active === '/employes'), justifyContent: 'space-between', padding: '8px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <User size={15} color={!activeService && active === '/employes' ? 'var(--blue)' : '#6b7280'} />Employés
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, background: '#f3f4f6', padding: '1px 6px', borderRadius: 8, color: '#6b7280' }}>{employes.length}</span>
              <span onClick={(e) => { e.stopPropagation(); setExpandedServices(v => !v); }} style={{ cursor: 'pointer', color: '#9ca3af', display: 'flex' }}>
                {expandedServices ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </span>
            </div>
          </div>
          {expandedServices && denominationsUtilisees.map(d => (
            <div key={d.label} onClick={() => handleServiceClick(d.label)}
              style={{ ...s.item(activeService === d.label), justifyContent: 'space-between', padding: '7px 20px 7px 28px' }}>
              <Folder size={13} color={activeService === d.label ? 'var(--blue)' : '#6b7280'} />{d.label}
              <span style={{ fontSize: 10, background: '#f3f4f6', padding: '1px 6px', borderRadius: 8, color: '#6b7280' }}>{d.count}</span>
            </div>
          ))}
          <div onClick={() => onNavigate('/clients')} style={{ ...s.item(active === '/clients'), padding: '8px 20px', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={15} color={active === '/clients' ? 'var(--blue)' : '#6b7280'} />Clients / Patients
            </div>
            <span style={{ fontSize: 10, background: '#f3f4f6', padding: '1px 6px', borderRadius: 8, color: '#6b7280' }}>{(clients || []).length}</span>
          </div>
        </>}

        {/* ANALYSE */}
        <div style={{ marginTop: 8 }}>
          <SectionHeader label="Analyse & Gestion" color="#0d9488" collapsed={collapsed.analyse} onToggle={() => toggle('analyse')} />
        </div>
        {!collapsed.analyse && <>
          {showEtab && (
            <div style={s.item(active === '/etablissement')} onClick={() => onNavigate('/etablissement')}>
              <Building2 size={15} color={active === '/etablissement' ? 'var(--blue)' : '#6b7280'} />Établissement
            </div>
          )}
          {showRestaurant && (
            <div style={s.item(active === '/restaurant')} onClick={() => onNavigate('/restaurant')}>
              <UtensilsCrossed size={15} color={active === '/restaurant' ? 'var(--blue)' : '#6b7280'} />Restaurant
            </div>
          )}
          <div style={s.item(active === '/rapports')} onClick={() => onNavigate('/rapports')}>
            <User size={15} color={active === '/rapports' ? 'var(--blue)' : '#6b7280'} />Employés
          </div>
          <div style={s.item(active === '/analyse-clients')} onClick={() => onNavigate('/analyse-clients')}>
            <Users size={15} color={active === '/analyse-clients' ? 'var(--blue)' : '#6b7280'} />Clients
          </div>
        </>}

        {/* ÉVÉNEMENTS */}
        <div style={{ marginTop: 8 }}>
          <SectionHeader label="Événements" color="#16a34a" collapsed={collapsed.evenements} onToggle={() => toggle('evenements')} />
        </div>
        {!collapsed.evenements && (
          <div style={s.item(active === '/copilote-ia')} onClick={() => onNavigate('/copilote-ia')}>
            <Bot size={15} color={active === '/copilote-ia' ? 'var(--blue)' : '#6b7280'} />Recherche
          </div>
        )}

        {/* RÉGLAGES */}
        <div style={{ marginTop: 8 }}>
          <SectionHeader label="Réglages" color="#dc2626" collapsed={collapsed.reglages} onToggle={() => toggle('reglages')} />
        </div>
        {!collapsed.reglages && <>
          <div style={s.item(active === '/communications')} onClick={() => onNavigate('/communications')}>
            <MessageSquare size={15} color={active === '/communications' ? 'var(--blue)' : '#6b7280'} />Communications
          </div>
          <div style={s.item(active === '/personnalisation')} onClick={() => onNavigate('/personnalisation')}>
            <Palette size={15} color={active === '/personnalisation' ? 'var(--blue)' : '#6b7280'} />Personnalisation
          </div>
        </>}

        {/* SYSTÈME */}
        <div style={{ marginTop: 8 }}>
          <SectionHeader label="Système" color="#b45309" collapsed={collapsed.systeme} onToggle={() => toggle('systeme')} />
        </div>
        {!collapsed.systeme && <>
          <div style={s.item(active === '/integrations')} onClick={() => onNavigate('/integrations')}>
            <Plug size={15} color={active === '/integrations' ? 'var(--blue)' : '#6b7280'} />Intégrations
          </div>
          <div style={s.item(active === '/reglages')} onClick={() => onNavigate('/reglages')}>
            <Settings size={15} color={active === '/reglages' ? 'var(--blue)' : '#6b7280'} />Compte
          </div>
        </>}

      </div>

      {/* Footer */}
      <div style={s.footer}>
        <button onClick={onLogout} style={{ width: '100%', background: 'transparent', border: '1px solid #fecaca', borderRadius: 7, padding: '7px', fontSize: 12, color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <LogOut size={13} color="#dc2626" />Déconnexion
        </button>
      </div>
    </aside>
  );
}
